import { useState, useEffect, useCallback, useRef } from 'react';
import api, { getErrorMessage } from '../services/api';
import Layout from '../components/Layout';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const toInputDate = (iso) => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const isBatchActive = (batch) => {
  if (!batch.end_date) return true;
  return new Date(batch.end_date) >= new Date(new Date().toDateString());
};

// ── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
    <div className="h-24 skeleton" />
    <div className="p-5 space-y-3">
      <div className="skeleton h-4 w-3/4" />
      <div className="skeleton h-3 w-1/2" />
      <div className="flex gap-3 mt-4">
        <div className="skeleton h-6 w-20 rounded-full" />
        <div className="skeleton h-6 w-24 rounded-full" />
      </div>
      <div className="flex gap-2 mt-4">
        <div className="skeleton h-9 w-full rounded-xl" />
        <div className="skeleton h-9 w-full rounded-xl" />
      </div>
    </div>
  </div>
);

const PageSkeleton = () => (
  <Layout>
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="skeleton h-8 w-48" />
        <div className="skeleton h-10 w-32 rounded-xl" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="rounded-2xl p-5 skeleton h-20" />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
      </div>
    </div>
  </Layout>
);

// ── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };
  const icons = {
    success: 'M5 13l4 4L19 7',
    error: 'M6 18L18 6M6 6l12 12',
  };

  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${styles[type]}`}>
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[type]} />
      </svg>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ── Confirm Modal ────────────────────────────────────────────────────────────

const ConfirmModal = ({ title, message, confirmLabel, confirmColor, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
    <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 p-6 w-full max-w-sm animate-fade-in">
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 mb-6">{message}</p>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className={`px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50 ${confirmColor}`}
        >
          {loading ? 'Deleting...' : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ── Batch Form Modal ─────────────────────────────────────────────────────────

const BatchFormModal = ({ batch, courses, onSave, onCancel, loading }) => {
  const isEdit = !!batch;
  const [form, setForm] = useState({
    course_id: batch?.course_id || (courses[0]?.id || ''),
    batch_name: batch?.batch_name || '',
    start_date: toInputDate(batch?.start_date) || '',
    end_date: toInputDate(batch?.end_date) || '',
  });
  const [formError, setFormError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.batch_name.trim()) return setFormError('Batch name is required.');
    if (!form.course_id) return setFormError('Please select a course.');
    if (!form.start_date) return setFormError('Start date is required.');
    if (!form.end_date) return setFormError('End date is required.');
    if (form.start_date > form.end_date) return setFormError('End date must be after start date.');
    onSave({
      course_id: form.course_id,
      batch_name: form.batch_name.trim(),
      start_date: form.start_date,
      end_date: form.end_date,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit Batch' : 'Add New Batch'}</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Course</label>
            <select
              value={form.course_id}
              onChange={(e) => { setForm({ ...form, course_id: e.target.value }); setFormError(''); }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white"
              required
            >
              <option value="">Select a course</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Batch Name</label>
            <input
              ref={inputRef}
              type="text"
              value={form.batch_name}
              onChange={(e) => { setForm({ ...form, batch_name: e.target.value }); setFormError(''); }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="e.g. Batch 2026 - Morning"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Start Date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => { setForm({ ...form, start_date: e.target.value }); setFormError(''); }}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">End Date</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => { setForm({ ...form, end_date: e.target.value }); setFormError(''); }}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                required
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Batch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Assign Trainer Modal ─────────────────────────────────────────────────────

const AssignTrainerModal = ({ batch, trainers, onSave, onCancel, loading }) => {
  const [trainerId, setTrainerId] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!trainerId) return;
    onSave(batch.id, trainerId);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Assign Trainer</h3>
            <p className="text-sm text-gray-500 mt-0.5">{batch.batch_name}</p>
          </div>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Select Trainer</label>
            <select
              value={trainerId}
              onChange={(e) => setTrainerId(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white"
              required
            >
              <option value="">Choose a trainer...</option>
              {trainers.map((t) => (
                <option key={t.id} value={t.id}>{t.full_name} ({t.email})</option>
              ))}
            </select>
          </div>

          {trainers.length === 0 && (
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-xl p-3">
              No trainers available. Please add trainers in User Management first.
            </p>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !trainerId}
              className="px-5 py-2.5 text-sm font-medium text-white bg-secondary rounded-xl hover:bg-secondary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Assigning...' : 'Assign Trainer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Students Modal ───────────────────────────────────────────────────────────

const StudentsModal = ({ batch, onClose, onToast, isTrainer }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Enroll state
  const [showEnroll, setShowEnroll] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [loadingAllStudents, setLoadingAllStudents] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');

  // Remove student state
  const [removingStudentId, setRemovingStudentId] = useState(null);
  const [removeError, setRemoveError] = useState('');

  const fetchEnrolled = useCallback(async () => {
    try {
      const res = await api.get(`/api/batches/${batch.id}/students`);
      setStudents(res.data || []);
    } catch {
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [batch.id]);

  useEffect(() => {
    fetchEnrolled();
  }, [fetchEnrolled]);

  const handleOpenEnroll = async () => {
    setShowEnroll(true);
    setSelectedStudentId('');
    setEnrollError('');
    if (allStudents.length) return; // already fetched
    setLoadingAllStudents(true);
    try {
      const res = await api.get('/api/users?role=student&per_page=200');
      setAllStudents(res.data.users || res.data || []);
    } catch {
      setAllStudents([]);
    } finally {
      setLoadingAllStudents(false);
    }
  };

  const handleEnroll = async () => {
    if (!selectedStudentId) return;
    setEnrolling(true);
    setEnrollError('');
    try {
      await api.post(`/api/batches/${batch.id}/enroll`, { student_id: selectedStudentId });
      setSelectedStudentId('');
      setShowEnroll(false);
      setLoading(true);
      await fetchEnrolled();
      if (onToast) onToast('Student enrolled successfully');
    } catch (err) {
      setEnrollError(getErrorMessage(err, 'Failed to enroll student'));
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemoveStudent = async (studentId) => {
    setRemovingStudentId(studentId);
    setRemoveError('');
    try {
      await api.delete(`/api/batches/${batch.id}/students/${studentId}`);
      await fetchEnrolled();
      if (onToast) onToast('Student removed successfully');
    } catch (err) {
      setRemoveError(getErrorMessage(err, 'Failed to remove student'));
    } finally {
      setRemovingStudentId(null);
    }
  };

  // Filter out already-enrolled students from the dropdown
  const enrolledIds = new Set(students.map((s) => s.id));
  const availableStudents = allStudents.filter((s) => !enrolledIds.has(s.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Enrolled Students</h3>
            <p className="text-sm text-gray-500 mt-0.5">{batch.batch_name}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* ── Enroll Student Section ──────────────────────────────── */}
          {!isTrainer && (!showEnroll ? (
            <button
              onClick={handleOpenEnroll}
              className="w-full mb-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Enroll Student
            </button>
          ) : (
            <div className="mb-5 p-4 bg-primary/5 border border-primary/20 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Enroll a Student</p>
                <button
                  onClick={() => { setShowEnroll(false); setEnrollError(''); }}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {enrollError && (
                <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">{enrollError}</div>
              )}

              {loadingAllStudents ? (
                <div className="flex items-center gap-2 py-2">
                  <svg className="animate-spin w-4 h-4 text-primary" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span className="text-xs text-gray-500">Loading students...</span>
                </div>
              ) : availableStudents.length === 0 ? (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  {allStudents.length === 0
                    ? 'No students found. Add students in User Management first.'
                    : 'All students are already enrolled in this batch.'}
                </p>
              ) : (
                <div className="flex gap-2">
                  <select
                    value={selectedStudentId}
                    onChange={(e) => { setSelectedStudentId(e.target.value); setEnrollError(''); }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white"
                  >
                    <option value="">Select student...</option>
                    {availableStudents.map((s) => (
                      <option key={s.id} value={s.id}>{s.full_name} ({s.email})</option>
                    ))}
                  </select>
                  <button
                    onClick={handleEnroll}
                    disabled={!selectedStudentId || enrolling}
                    className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    {enrolling ? 'Enrolling...' : 'Enroll'}
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* ── Student List ───────────────────────────────────────── */}
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="skeleton h-9 w-9 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <div className="skeleton h-4 w-32" />
                    <div className="skeleton h-3 w-48" />
                  </div>
                </div>
              ))}
            </div>
          ) : students.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">No students enrolled</p>
              <p className="text-xs text-gray-400 mt-1">Use the button above to enroll students</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{students.length} enrolled</p>
              {removeError && (
                <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs mb-2">{removeError}</div>
              )}
              {students.map((s, idx) => (
                <div key={s.id || idx} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">
                      {(s.full_name || s.email || '?')[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{s.full_name || 'Unknown'}</p>
                    <p className="text-xs text-gray-400 truncate">{s.email}</p>
                  </div>
                  {s.phone && (
                    <span className="text-xs text-gray-400 hidden sm:block">{s.phone}</span>
                  )}
                  {!isTrainer && (
                    <button
                      onClick={() => handleRemoveStudent(s.id)}
                      disabled={removingStudentId === s.id}
                      className="flex-shrink-0 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                      title="Remove student"
                    >
                      {removingStudentId === s.id ? (
                        <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

const BatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Modals
  const [showForm, setShowForm] = useState(false);
  const [editBatch, setEditBatch] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [studentsTarget, setStudentsTarget] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Role
  const isTrainer = JSON.parse(localStorage.getItem('user') || '{}')?.role === 'trainer';

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  // ── Fetch ──────────────────────────────────────────────────────────────

  const fetchBatches = useCallback(async () => {
    try {
      const res = await api.get('/api/batches');
      setBatches(res.data || []);
      setError('');
    } catch {
      setError('Failed to load batches.');
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const res = await api.get('/api/courses');
      setCourses(res.data || []);
    } catch {
      // non-critical
    }
  }, []);

  const fetchTrainers = useCallback(async () => {
    try {
      const res = await api.get('/api/users?role=trainer&per_page=100');
      setTrainers(res.data.users || res.data || []);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchBatches(), fetchCourses(), fetchTrainers()]);
      setLoading(false);
    };
    load();
  }, [fetchBatches, fetchCourses, fetchTrainers]);

  // ── Computed stats ────────────────────────────────────────────────────

  const totalBatches = batches.length;
  const activeBatches = batches.filter(isBatchActive).length;
  const totalStudents = batches.reduce((sum, b) => sum + (b.student_count || b.enrolled_count || 0), 0);

  // ── Filtered list ─────────────────────────────────────────────────────

  const filtered = search.trim()
    ? batches.filter(
        (b) =>
          b.batch_name.toLowerCase().includes(search.toLowerCase()) ||
          (b.course_title || '').toLowerCase().includes(search.toLowerCase())
      )
    : batches;

  // ── Actions ───────────────────────────────────────────────────────────

  const handleCreate = async (form) => {
    setModalLoading(true);
    try {
      await api.post('/api/batches', form);
      setShowForm(false);
      showToast('Batch created successfully');
      fetchBatches();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to create batch'), 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEdit = async (form) => {
    setModalLoading(true);
    try {
      await api.put(`/api/batches/${editBatch.id}`, form);
      setEditBatch(null);
      showToast('Batch updated successfully');
      fetchBatches();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to update batch'), 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async () => {
    setModalLoading(true);
    try {
      await api.delete(`/api/batches/${deleteTarget.id}`);
      setDeleteTarget(null);
      showToast('Batch deleted successfully');
      fetchBatches();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to delete batch'), 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleAssignTrainer = async (batchId, trainerId) => {
    setModalLoading(true);
    try {
      await api.post(`/api/batches/${batchId}/assign-trainer`, { trainer_id: trainerId });
      setAssignTarget(null);
      showToast('Trainer assigned successfully');
      fetchBatches();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to assign trainer'), 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleRemoveTrainer = async (batchId, trainerId) => {
    try {
      await api.delete(`/api/batches/${batchId}/trainers/${trainerId}`);
      showToast('Trainer removed successfully');
      fetchBatches();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to remove trainer'), 'error');
    }
  };

  // ── Loading / Error ───────────────────────────────────────────────────

  if (loading) return <PageSkeleton />;

  if (error && !batches.length) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Failed to load batches</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Layout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="space-y-6 animate-fade-in">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Batch Management</h1>
            <p className="text-sm text-gray-500 mt-1">Create and manage your training batches</p>
          </div>
          {!isTrainer && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Batch
            </button>
          )}
        </div>

        {/* ── Stats ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total Batches', value: totalBatches, icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', color: 'from-[#003479] to-[#0052b4]' },
            { label: 'Active Batches', value: activeBatches, icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: 'from-[#20C997] to-emerald-600' },
            { label: 'Total Students', value: totalStudents, icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', color: 'from-[#1B9AAA] to-[#005792]' },
          ].map((s) => (
            <div key={s.label} className={`bg-gradient-to-br ${s.color} rounded-2xl p-5 text-white shadow-lg`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{(s.value ?? 0).toLocaleString()}</p>
                  <p className="text-sm opacity-90 mt-0.5">{s.label}</p>
                </div>
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={s.icon} />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Search ──────────────────────────────────────────────────── */}
        <div className="relative max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search batches..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
          />
        </div>

        {/* ── Batch Grid ─────────────────────────────────────────────── */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">
                {search ? 'No batches match your search' : 'No batches yet'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {search ? 'Try a different keyword' : 'Create your first batch to get started'}
              </p>
              {!search && !isTrainer && (
                <button
                  onClick={() => setShowForm(true)}
                  className="mt-5 px-5 py-2 bg-primary/10 text-primary text-sm font-medium rounded-xl hover:bg-primary/20 transition-colors"
                >
                  Create Batch
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((batch) => {
              const active = isBatchActive(batch);
              const studentCount = batch.student_count || batch.enrolled_count || 0;

              return (
                <div
                  key={batch.id}
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200 flex flex-col overflow-hidden group"
                >
                  {/* Gradient header */}
                  <div className={`px-5 py-4 bg-gradient-to-r ${active ? 'from-[#20C997] to-[#003479]' : 'from-gray-400 to-gray-500'}`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-bold text-white truncate">{batch.batch_name}</h3>
                        <p className="text-sm text-white/80 truncate mt-0.5">
                          {batch.course_title || 'No course assigned'}
                        </p>
                      </div>
                      <span className={`ml-2 flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${active ? 'bg-white/25 text-white' : 'bg-white/20 text-white'}`}>
                        {active ? 'Active' : 'Ended'}
                      </span>
                    </div>
                  </div>

                  <div className="p-5 flex flex-col flex-1">
                    {/* Dates */}
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{formatDate(batch.start_date)} — {formatDate(batch.end_date)}</span>
                    </div>

                    {/* Stats chips */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        {studentCount} {studentCount === 1 ? 'Student' : 'Students'}
                      </span>
                    </div>

                    {/* Trainers */}
                    <div className="flex-1 mb-4">
                      {batch.trainers && batch.trainers.length > 0 ? (
                        <div className="space-y-1">
                          {batch.trainers.map((t, i) => (
                            <div key={t.id || i} className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <div className="w-6 h-6 rounded-full bg-secondary/15 flex items-center justify-center flex-shrink-0">
                                  <span className="text-[10px] font-bold text-secondary">
                                    {(t.full_name || t.email || '?')[0].toUpperCase()}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 truncate">{t.full_name || t.email}</p>
                              </div>
                              {!isTrainer && (
                                <button
                                  onClick={() => handleRemoveTrainer(batch.id, t.id)}
                                  className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Remove trainer"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic">No trainer assigned</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
                      {!isTrainer && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditBatch(batch)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                          <button
                            onClick={() => setDeleteTarget(batch)}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => setStudentsTarget(batch)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-primary bg-primary/10 rounded-xl hover:bg-primary/20 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          View Students
                        </button>
                        {!isTrainer && (
                          <button
                            onClick={() => setAssignTarget(batch)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-secondary bg-secondary/10 rounded-xl hover:bg-secondary/20 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                            Assign Trainer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Modals ───────────────────────────────────────────────────── */}
      {showForm && (
        <BatchFormModal
          batch={null}
          courses={courses}
          onSave={handleCreate}
          onCancel={() => setShowForm(false)}
          loading={modalLoading}
        />
      )}

      {editBatch && (
        <BatchFormModal
          batch={editBatch}
          courses={courses}
          onSave={handleEdit}
          onCancel={() => setEditBatch(null)}
          loading={modalLoading}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Batch"
          message={`Are you sure you want to delete "${deleteTarget.batch_name}"? All enrollments in this batch will also be removed. This cannot be undone.`}
          confirmLabel="Delete"
          confirmColor="bg-red-600 hover:bg-red-700"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={modalLoading}
        />
      )}

      {assignTarget && (
        <AssignTrainerModal
          batch={assignTarget}
          trainers={trainers}
          onSave={handleAssignTrainer}
          onCancel={() => setAssignTarget(null)}
          loading={modalLoading}
        />
      )}

      {studentsTarget && (
        <StudentsModal
          batch={studentsTarget}
          onClose={() => setStudentsTarget(null)}
          onToast={(msg) => { showToast(msg); fetchBatches(); }}
          isTrainer={isTrainer}
        />
      )}
    </Layout>
  );
};

export default BatchManagement;
